import { DOCUMENT, Injectable, InjectionToken, PLATFORM_ID, inject, RESPONSE_INIT } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { EMPTY, Observable, map } from 'rxjs';
import { isPlatformServer } from '@angular/common';
import { notFoundPath } from '../app.routes';
import { Router } from '@angular/router';
import { BaseLocationService } from './base-location.service';
import { EXPECTED_HTTP_STATUS_CODES } from './http-logger.interceptor';

export type UrlTransformers = {
  searchRegex: RegExp;
  replaceValue: string;
}[];

export interface ContentRepositoryServiceConfigurationInterface {
  urlTransformers: UrlTransformers;
  serverUrlTransformers?: UrlTransformers;
}

export class MetaRefreshRedirectError extends Error {
  public constructor(
    public readonly location: string,
  ) {
    super(`Meta refresh redirect to ${location}`);
    this.name = 'MetaRefreshRedirectError';
  }
}

export const contentRepositoryServiceConfiguration = new InjectionToken<ContentRepositoryServiceConfigurationInterface>('contentRepositoryServiceConfiguration');

@Injectable({
  providedIn: 'root',
})
export class ContentRepositoryService {

  private configuration = inject(contentRepositoryServiceConfiguration);
  private http = inject(HttpClient);
  private baseLocationService = inject(BaseLocationService);
  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);
  private readonly urlTransformers = this.configuration.serverUrlTransformers && isPlatformServer(this.platformId) ? this.configuration.serverUrlTransformers : this.configuration.urlTransformers;

  private response = inject(RESPONSE_INIT, {
    optional: true,
  });

  private replaceVariables(url: string): string { // TODO: check if this can be abused on the server
    return url
      .replace('${location.host}', this.baseLocationService.host)
      .replace('${location.hostname}', this.baseLocationService.hostname)
      .replace('${location.port}', this.baseLocationService.port)
      .replace('${location.protocol}', this.baseLocationService.protocol)
      .replace('${location.origin}', this.baseLocationService.origin)
    ;
  }

  private applyTransformers(url: string): string {
    this.urlTransformers.forEach((urlTransformer) => {
      url = url.replace(urlTransformer.searchRegex, urlTransformer.replaceValue)
    });
    return url;
  }

  public fetch<T>(url: string, expectNotFound = false): Observable<T> {
    url = this.applyTransformers(url);
    url = this.replaceVariables(url);
    return this.http.get<T>(url, {
      withCredentials: true,
      context: expectNotFound ? new HttpContext().set(EXPECTED_HTTP_STATUS_CODES, [404]) : undefined,
    });
  }

  public fetchPage<T>(url: string, expectNotFound = false): Observable<T> {
    url = this.applyTransformers(url);
    url = this.replaceVariables(url);

    return this.http.get(url, {
      observe: 'response',
      responseType: 'text',
      withCredentials: true,
      context: expectNotFound ? new HttpContext().set(EXPECTED_HTTP_STATUS_CODES, [404]) : undefined,
    }).pipe(
      map((response) => this.parsePageResponse<T>(response)),
    );
  }

  public handleError(error: unknown, locale: string, region?: string, expectNotFound = false): Observable<never> {
    if (error instanceof HttpErrorResponse) {
      return this.handleHttpError(error, locale, region, expectNotFound);
    } else {
      this.handleUnknownError(error);
    }
    throw error;
  }

  public handleUnknownError(error: unknown): Observable<never> {
    if (isPlatformServer(this.platformId) && this.response) {
      this.response.status = 500;
    }
    throw error;
  }

  private parsePageResponse<T>(response: HttpResponse<string>): T {
    const body = response.body ?? '';
    const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';

    if (contentType.includes('text/html') || this.looksLikeHtml(body)) {
      const redirectLocation = this.extractMetaRefreshLocation(body, response.url ?? undefined);
      if (redirectLocation) {
        throw new MetaRefreshRedirectError(redirectLocation);
      }

      throw new Error('Expected JSON page content but received HTML without a meta refresh redirect');
    }

    return JSON.parse(body) as T;
  }

  private looksLikeHtml(body: string): boolean {
    return /<html[\s>]|<meta[\s>]|<!doctype html/i.test(body);
  }

  private extractMetaRefreshLocation(body: string, responseUrl?: string): string | null {
    const metaRefreshMatch = body.match(/<meta\b[^>]*http-equiv\s*=\s*['"]?refresh['"]?[^>]*content\s*=\s*(['"])(.*?)\1[^>]*>/i)
      ?? body.match(/<meta\b[^>]*content\s*=\s*(['"])(.*?)\1[^>]*http-equiv\s*=\s*['"]?refresh['"]?[^>]*>/i);

    const content = metaRefreshMatch?.[2];
    if (!content) {
      return null;
    }

    const urlMatch = content.match(/url\s*=\s*(.*)$/i);
    const rawTarget = urlMatch?.[1]?.trim().replace(/^['"]|['"]$/g, '');
    if (!rawTarget) {
      return null;
    }

    const baseUrl = responseUrl ?? this.document.location?.href ?? this.baseLocationService.origin;
    try {
      return new URL(rawTarget, baseUrl).toString();
    } catch {
      return rawTarget;
    }
  }

  private router = inject(Router);

  public handleHttpError(error: HttpErrorResponse, locale: string, region?: string, expectNotFound = false): Observable<never> {
    const statusCode = error.status === 404 && expectNotFound === false ? 500 : error.status;
    if (this.response) {
      this.response.status = statusCode;
    }

    switch(statusCode) {
      case 404:
        this.navigateToErrorPage(notFoundPath, locale, region);
        break;
      default:
        break;
    }

    return EMPTY; // error is logged with http-logger.interceptor
  }

  private navigateToErrorPage(errorPage: string, locale: string, region?: string, error?: unknown): void {
    const errorUrl = [locale];
    if (region) {
      errorUrl.push(region.toLowerCase());
    }
    errorUrl.push(errorPage);
    this.router.navigate(errorUrl, {
      skipLocationChange: true,
      state: error ? {
        error: error,
      } : undefined,
    });
  }

}
